import React, { useState, useRef, useEffect } from 'react';
import { Star, MoreHorizontal, FileText, Table2, LayoutDashboard, PenLine, GitBranch, Bot, BarChart3, Pencil, Trash2, Copy, Eye, Share2, RotateCcw, Clock, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const typeConfig = {
  Form:     { icon: FileText,        bg: 'bg-[#FEF3E2]', color: 'text-[#C2410C]' },
  Table:    { icon: Table2,          bg: 'bg-[#E6F7EE]', color: 'text-[#0AA24F]' },
  App:      { icon: LayoutDashboard, bg: 'bg-[#FFE7F1]', color: 'text-[#E5387B]' },
  Sign:     { icon: PenLine,         bg: 'bg-[#E1F5F3]', color: 'text-[#0E9C8E]' },
  Workflow: { icon: GitBranch,       bg: 'bg-[#F0E9FF]', color: 'text-[#7C5CFF]' },
  Agent:    { icon: Bot,             bg: 'bg-[#E8ECFF]', color: 'text-[#4460F7]' },
  Report:   { icon: BarChart3,       bg: 'bg-[#E4F1FF]', color: 'text-[#2D8DF0]' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}

function daysLeft(deletedAt) {
  if (!deletedAt) return 30;
  const expires = new Date(deletedAt).getTime() + 30 * 86400000;
  return Math.max(0, Math.ceil((expires - Date.now()) / 86400000));
}

const TrashMenu = ({ open, onClose, onRestore, onPermanentDelete }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-30 w-48 bg-white border border-[#E3E5F5] rounded-lg shadow-lg py-1">
      <button onClick={onRestore} data-testid="menu-restore" className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#0A1551] hover:bg-[#F5F6FB] text-left">
        <RotateCcw className="w-3.5 h-3.5" /> Restore
      </button>
      <div className="my-1 border-t border-[#E3E5F5]" />
      <button onClick={onPermanentDelete} data-testid="menu-permanent-delete" className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#E5387B] hover:bg-[#FEF2F2] text-left">
        <Trash2 className="w-3.5 h-3.5" /> Delete permanently
      </button>
    </div>
  );
};

const TrashCard = ({ asset, onRestore, onPermanentDelete }) => {
  const cfg = typeConfig[asset.type] || typeConfig.Form;
  const Icon = cfg.icon;
  const [menuOpen, setMenuOpen] = useState(false);
  const left = daysLeft(asset.deleted_at);
  return (
    <div className="group bg-white border border-[#E3E5F5] rounded-xl overflow-hidden opacity-90 hover:opacity-100 transition-opacity" data-testid={`trash-card-${asset.id}`}>
      <div className={`h-[130px] ${cfg.bg} flex items-center justify-center relative grayscale-[40%]`}>
        <Icon className={`w-12 h-12 ${cfg.color}`} strokeWidth={1.5} />
        <span className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${left <= 5 ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-white/90 text-[#6F76A7]'}`} data-testid="trash-days-left">
          <Clock className="w-3 h-3" /> {left} day{left === 1 ? '' : 's'} left
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-[14px] font-semibold text-[#0A1551] leading-snug line-clamp-2 flex-1">{asset.name}</h3>
          <div className="relative shrink-0">
            <button onClick={() => setMenuOpen((v) => !v)} data-testid="trash-card-menu" className="p-1 rounded hover:bg-[#F0F1FB]" aria-label="More">
              <MoreHorizontal className="w-4 h-4 text-[#6F76A7]" />
            </button>
            <TrashMenu open={menuOpen} onClose={() => setMenuOpen(false)}
              onRestore={() => { setMenuOpen(false); onRestore(asset.id); }}
              onPermanentDelete={() => { setMenuOpen(false); onPermanentDelete(asset.id); }} />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} text-[11px] font-semibold`}>{asset.type}</span>
          <span className="text-[12px] text-[#6F76A7]">Deleted {timeAgo(asset.deleted_at)}</span>
        </div>
        <button onClick={() => onRestore(asset.id)} data-testid="trash-restore-button" className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-[#D5D7ED] text-[13px] font-medium text-[#0A1551] hover:bg-[#F5F6FB]">
          <RotateCcw className="w-3.5 h-3.5" /> Restore
        </button>
      </div>
    </div>
  );
};

const RowMenu = ({ open, onClose, onRename, onDelete, onPreview, onDuplicate, onShare, position = 'right' }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div ref={ref} className={`absolute ${position === 'right' ? 'right-0' : 'left-0'} top-full mt-1 z-30 w-44 bg-white border border-[#E3E5F5] rounded-lg shadow-lg py-1`}>
      <button onClick={onPreview} className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#0A1551] hover:bg-[#F5F6FB] text-left">
        <Eye className="w-3.5 h-3.5" /> Open
      </button>
      <button onClick={onRename} className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#0A1551] hover:bg-[#F5F6FB] text-left">
        <Pencil className="w-3.5 h-3.5" /> Rename
      </button>
      <button onClick={onDuplicate} className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#0A1551] hover:bg-[#F5F6FB] text-left">
        <Copy className="w-3.5 h-3.5" /> Duplicate
      </button>
      <button onClick={onShare} className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#0A1551] hover:bg-[#F5F6FB] text-left">
        <Share2 className="w-3.5 h-3.5" /> Copy share link
      </button>
      <div className="my-1 border-t border-[#E3E5F5]" />
      <button onClick={onDelete} data-testid="menu-delete" className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#E5387B] hover:bg-[#FEF2F2] text-left">
        <Trash2 className="w-3.5 h-3.5" /> Move to trash
      </button>
    </div>
  );
};

const LabelChips = ({ asset, labels, onRemoveLabel }) => {
  const mine = (asset.label_ids || []).map((id) => labels.find((l) => l.id === id)).filter(Boolean);
  if (!mine.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2" data-testid={`asset-labels-${asset.id}`}>
      {mine.map((l) => (
        <span key={l.id} className="group/chip inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full text-[11px] font-medium border" style={{ borderColor: l.color + '55', color: l.color, backgroundColor: l.color + '12' }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: l.color }} />
          {l.name}
          <button onClick={(e) => { e.stopPropagation(); onRemoveLabel(asset.id, l.id); }} className="opacity-0 group-hover/chip:opacity-100 hover:bg-white/60 rounded-full" aria-label={`Remove label ${l.name}`} data-testid={`remove-label-${asset.id}-${l.id}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
};

const dragProps = (asset) => ({
  draggable: true,
  onDragStart: (e) => { e.dataTransfer.setData('text/asset-id', asset.id); e.dataTransfer.effectAllowed = 'link'; },
});

const AssetCard = ({ asset, onStar, onRename, onDelete, onPreview, onDuplicate, onShare, labels, onRemoveLabel, currentUserId }) => {
  const cfg = typeConfig[asset.type] || typeConfig.Form;
  const Icon = cfg.icon;
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(asset.name);

  const commit = () => {
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== asset.name) onRename(asset.id, trimmed);
    else setName(asset.name);
  };

  return (
    <div {...dragProps(asset)} className="group bg-white border border-[#E3E5F5] rounded-xl hover:shadow-md hover:border-[#C8CCE8] transition-all overflow-hidden cursor-grab active:cursor-grabbing" data-testid={`asset-card-${asset.id}`}>
      <div className={`h-[130px] ${cfg.bg} flex items-center justify-center relative cursor-pointer`} onClick={() => onPreview(asset)}>
        <Icon className={`w-12 h-12 ${cfg.color}`} strokeWidth={1.5} />
        {asset.owner_id && currentUserId && asset.owner_id !== currentUserId && (
          <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 text-[11px] font-semibold text-[#6F76A7]" data-testid="shared-badge"><Users className="w-3 h-3" /> Shared</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onStar(asset.id, !asset.starred); }}
          data-testid="asset-star-button"
          className="absolute top-2 right-2 w-7 h-7 rounded-md bg-white/0 group-hover:bg-white/80 flex items-center justify-center transition-colors"
          aria-label="Toggle star"
        >
          <Star className={`w-4 h-4 ${asset.starred ? 'fill-[#FFB800] text-[#FFB800]' : 'text-[#6F76A7]'}`} />
        </button>
      </div>

      <div className="p-4 cursor-pointer" onClick={() => onPreview(asset)}>
        <div className="flex items-start justify-between gap-2">
          {editing ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commit}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setName(asset.name); setEditing(false); } }}
              className="flex-1 text-[14px] font-semibold text-[#0A1551] px-1.5 py-0.5 border border-[#0A1551] rounded outline-none"
            />
          ) : (
            <h3 onClick={(e) => e.stopPropagation()} onDoubleClick={() => setEditing(true)} title="Double-click to rename" className="text-[14px] font-semibold text-[#0A1551] leading-snug line-clamp-2 flex-1 cursor-text">
              {asset.name}
            </h3>
          )}
          <div className="relative shrink-0">
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }} data-testid="asset-card-menu" className="p-1 rounded hover:bg-[#F0F1FB]" aria-label="More">
              <MoreHorizontal className="w-4 h-4 text-[#6F76A7]" />
            </button>
            <RowMenu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              onRename={() => { setEditing(true); setMenuOpen(false); }}
              onDelete={() => { setMenuOpen(false); onDelete(asset.id); }}
              onPreview={() => { setMenuOpen(false); onPreview(asset); }}
              onDuplicate={() => { setMenuOpen(false); onDuplicate(asset.id); }}
              onShare={() => { setMenuOpen(false); onShare(asset); }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} text-[11px] font-semibold`}>
            {asset.type}
          </span>
          <span className="text-[12px] text-[#6F76A7]">{timeAgo(asset.updated_at) || asset.updatedAt}</span>
        </div>
        <LabelChips asset={asset} labels={labels} onRemoveLabel={onRemoveLabel} />
        <div className="mt-3 pt-3 border-t border-[#F0F1FB] flex items-center justify-between text-[12px]">
          <span className="text-[#6F76A7]">{asset.submissions} submissions</span>
          <span className={`font-medium ${asset.status === 'Enabled' ? 'text-[#0AA24F]' : 'text-[#B0B4D0]'}`}>{asset.status}</span>
        </div>
      </div>
    </div>
  );
};

const AssetRow = ({ asset, onStar, onRename, onDelete, onPreview, onDuplicate, onShare, labels, onRemoveLabel }) => {
  const cfg = typeConfig[asset.type] || typeConfig.Form;
  const Icon = cfg.icon;
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(asset.name);

  const commit = () => {
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== asset.name) onRename(asset.id, trimmed);
    else setName(asset.name);
  };

  return (
    <div {...dragProps(asset)} className="grid grid-cols-12 gap-4 items-center px-4 py-3 border-b border-[#E3E5F5] hover:bg-[#F9FAFD] transition-colors cursor-grab active:cursor-grabbing" data-testid={`asset-row-${asset.id}`}>
      <div className="col-span-5 flex items-center gap-3 cursor-pointer" onClick={() => !editing && onPreview(asset)}>
        <div className={`w-9 h-9 rounded-md ${cfg.bg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 ${cfg.color}`} />
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setName(asset.name); setEditing(false); } }}
              className="w-full text-[14px] font-semibold text-[#0A1551] px-1.5 py-0.5 border border-[#0A1551] rounded outline-none"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="text-[14px] font-semibold text-[#0A1551] truncate">{asset.name}</div>
          )}
          <div className="text-[12px] text-[#6F76A7]">Created {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : ''}</div>
          <LabelChips asset={asset} labels={labels} onRemoveLabel={onRemoveLabel} />
        </div>
      </div>
      <div className="col-span-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${cfg.bg} ${cfg.color} text-[11px] font-semibold`}>
          {asset.type}
        </span>
      </div>
      <div className="col-span-2 text-[13px] text-[#4A517D]">{asset.submissions} submissions</div>
      <div className="col-span-2 text-[13px] text-[#6F76A7]">{timeAgo(asset.updated_at)}</div>
      <div className="col-span-1 flex items-center justify-end gap-1 relative">
        <button onClick={() => onStar(asset.id, !asset.starred)} className="p-1 rounded hover:bg-[#F0F1FB]" aria-label="Star">
          <Star className={`w-4 h-4 ${asset.starred ? 'fill-[#FFB800] text-[#FFB800]' : 'text-[#B0B4D0]'}`} />
        </button>
        <div className="relative">
          <button onClick={() => setMenuOpen((v) => !v)} className="p-1 rounded hover:bg-[#F0F1FB]" aria-label="More">
            <MoreHorizontal className="w-4 h-4 text-[#6F76A7]" />
          </button>
          <RowMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            onRename={() => { setEditing(true); setMenuOpen(false); }}
            onDelete={() => { setMenuOpen(false); onDelete(asset.id); }}
            onPreview={() => { setMenuOpen(false); onPreview(asset); }}
            onDuplicate={() => { setMenuOpen(false); onDuplicate(asset.id); }}
            onShare={() => { setMenuOpen(false); onShare(asset); }}
          />
        </div>
      </div>
    </div>
  );
};

const AssetGrid = ({ assets, view, onStar, onRename, onDelete, onPreview, onDuplicate, onRestore, onPermanentDelete, trashMode, loading, labels = [], onRemoveLabel, currentUserId }) => {
  const onShare = (asset) => {
    const url = `${window.location.origin}/f/${asset.share_id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    toast.success('Share link copied to clipboard', { description: url });
  };
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={`skeleton-${i}`} className="bg-white border border-[#E3E5F5] rounded-xl overflow-hidden animate-pulse">
            <div className="h-[130px] bg-[#F0F1FB]" />
            <div className="p-4 space-y-2">
              <div className="h-3 bg-[#F0F1FB] rounded w-3/4" />
              <div className="h-3 bg-[#F0F1FB] rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!assets || assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-state">
        <div className="w-24 h-24 rounded-full bg-[#F0F1FB] flex items-center justify-center mb-4">
          {trashMode ? <Trash2 className="w-10 h-10 text-[#B0B4D0]" strokeWidth={1.5} /> : <FileText className="w-10 h-10 text-[#B0B4D0]" strokeWidth={1.5} />}
        </div>
        <h3 className="text-[18px] font-semibold text-[#0A1551]">{trashMode ? 'Trash is empty' : 'No assets found'}</h3>
        <p className="text-[14px] text-[#6F76A7] mt-1">{trashMode ? 'Deleted items stay here for 30 days before being removed.' : 'Click + CREATE to build your first form, or adjust your filters.'}</p>
      </div>
    );
  }

  if (trashMode) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="trash-grid">
        {assets.map((a) => <TrashCard key={a.id} asset={a} onRestore={onRestore} onPermanentDelete={onPermanentDelete} />)}
      </div>
    );
  }

  const commonProps = { onStar, onRename, onDelete, onPreview, onDuplicate, onShare, labels, onRemoveLabel, currentUserId };

  if (view === 'list') {
    return (
      <div className="bg-white border border-[#E3E5F5] rounded-xl overflow-visible">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-[#F9FAFD] border-b border-[#E3E5F5] text-[11px] font-semibold uppercase tracking-wider text-[#6F76A7]">
          <div className="col-span-5">Name</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Submissions</div>
          <div className="col-span-2">Last Activity</div>
          <div className="col-span-1"></div>
        </div>
        {assets.map((a) => (
          <AssetRow key={a.id} asset={a} {...commonProps} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {assets.map((a) => (
        <AssetCard key={a.id} asset={a} {...commonProps} />
      ))}
    </div>
  );
};

export default AssetGrid;
